import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function FastFilesLanding() {
  const [businessName, setBusinessName] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [email, setEmail] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdLink, setCreatedLink] = useState('');
  const navigate = useNavigate();

  const generateSlug = (name: string) => {
    // Add random 6-character prefix to prevent collisions
    // Format: {random-id}/{readable-name}
    const randomId = Math.random().toString(36).substring(2, 8);
    const readableName = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return `${randomId}/${readableName}`;
  };

  const handleBusinessNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setBusinessName(name);
    if (name) {
      setCustomUrl(generateSlug(name));
    } else {
      setCustomUrl('');
    }
  };

  const handleCreate = async () => {
    if (!businessName || !customUrl) return;

    setIsCreating(true);

    try {
      // Create the receiving page
      const response = await fetch('https://fastfiles-api.b-9f2.workers.dev/api/quick-upload-page', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          businessName,
          slug: customUrl,
          email: email || null
        })
      });

      const result = await response.json();

      if (result.success) {
        // Use the /files/ path that actually works
        const fullUrl = `https://fastfiles.app/files/${result.data.slug}`;
        setCreatedLink(fullUrl);
        setShowSuccess(true);

        // Store in localStorage for anonymous users
        if (!email) {
          const myLinks = JSON.parse(localStorage.getItem('myFileReceivers') || '[]');
          myLinks.push({
            id: result.data.id,
            slug: result.data.slug,
            name: businessName,
            url: fullUrl,
            createdAt: new Date().toISOString()
          });
          localStorage.setItem('myFileReceivers', JSON.stringify(myLinks));
        }
      }
    } catch (error) {
      console.error('Error creating page:', error);
      alert('Failed to create page. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(createdLink);
    // Show copied feedback
    const btn = document.getElementById('copy-btn');
    if (btn) {
      btn.textContent = '✓ Copied!';
      setTimeout(() => {
        btn.textContent = 'Copy Link';
      }, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">FastFiles</h1>
              <span className="ml-3 text-sm text-gray-500">File sharing made simple</span>
            </div>
            <nav className="flex gap-6">
              <a href="/dashboard" className="text-gray-600 hover:text-gray-900">Dashboard</a>
              <a href="/pricing" className="text-gray-600 hover:text-gray-900">Pricing</a>
              <a href="/login" className="text-blue-600 hover:text-blue-700 font-medium">Sign In</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-16">
        {!showSuccess ? (
          <>
            {/* Hero Section */}
            <div className="text-center mb-12">
              <h2 className="text-5xl font-bold text-gray-900 mb-4">
                Receive files from anyone
              </h2>
              <p className="text-xl text-gray-600">
                Create a file upload page in seconds. No sign up required.
              </p>
            </div>

            {/* Creation Form */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 max-w-2xl mx-auto">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What are you collecting files for?
                </label>
                <input
                  type="text"
                  value={businessName}
                  onChange={handleBusinessNameChange}
                  placeholder="e.g., Wedding Photos, Tax Documents, Design Assets"
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
              </div>

              {customUrl && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your upload page URL
                  </label>
                  <div className="flex items-center bg-gray-50 rounded-lg px-4 py-3 border border-gray-300">
                    <span className="text-gray-500 text-sm">fastfiles.app/files/</span>
                    <input
                      type="text"
                      value={customUrl}
                      onChange={(e) => setCustomUrl(e.target.value)}
                      className="flex-1 bg-transparent border-none focus:outline-none ml-1"
                    />
                  </div>
                </div>
              )}

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email (optional - to manage your files)
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="mt-1 text-sm text-gray-500">
                  We'll send you a link to manage your uploaded files
                </p>
              </div>

              <button
                onClick={handleCreate}
                disabled={!businessName || !customUrl || isCreating}
                className={`w-full py-4 text-lg font-medium rounded-lg transition-all ${
                  businessName && customUrl && !isCreating
                    ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isCreating ? 'Creating...' : 'Create File Upload Page'}
              </button>
            </div>

            {/* Features */}
            <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-4xl mb-3">⚡</div>
                <h3 className="font-semibold text-gray-900 mb-2">Instant Setup</h3>
                <p className="text-gray-600">No account needed. Your page is ready in seconds.</p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">🔒</div>
                <h3 className="font-semibold text-gray-900 mb-2">Secure Storage</h3>
                <p className="text-gray-600">Files stored securely in CloudFlare R2.</p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">📱</div>
                <h3 className="font-semibold text-gray-900 mb-2">Works Everywhere</h3>
                <p className="text-gray-600">Beautiful interface on any device.</p>
              </div>
            </div>
          </>
        ) : (
          /* Success State */
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>

            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Your file upload page is ready!
            </h2>

            <p className="text-xl text-gray-600 mb-8">
              Share this link to start receiving files
            </p>

            <div className="bg-gray-50 rounded-lg p-6 max-w-2xl mx-auto mb-8">
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={createdLink}
                  readOnly
                  className="flex-1 px-4 py-3 bg-white border border-gray-300 rounded-lg font-mono text-sm"
                />
                <button
                  id="copy-btn"
                  onClick={handleCopyLink}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  Copy Link
                </button>
                <a
                  href={createdLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                >
                  Visit Page
                </a>
              </div>
            </div>

            {email && (
              <div className="bg-blue-50 rounded-lg p-6 max-w-2xl mx-auto">
                <p className="text-blue-900">
                  📧 We've sent a management link to <strong>{email}</strong>
                </p>
                <p className="text-blue-700 text-sm mt-2">
                  Check your email to access your dashboard and manage uploaded files.
                </p>
              </div>
            )}

            <div className="mt-12">
              <button
                onClick={() => {
                  setShowSuccess(false);
                  setBusinessName('');
                  setCustomUrl('');
                  setEmail('');
                }}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                ← Create Another
              </button>
              {' • '}
              <a href="/dashboard" className="text-blue-600 hover:text-blue-700 font-medium">
                Go to Dashboard →
              </a>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}